import { FormGroup } from '@ksyos/design-system';
import { useContext, useEffect, useState } from 'react';
import type { IAddCarePathServiceConsumerSettings } from '@common/interfaces/addCarePathServicesSettings';
import type { IAutoSubsequentService, IProviderOrganization } from '@common/interfaces/autoSubsequentService';
import type { TValueOfAddCarePathSettings } from '../../../../../../common/interfaces/addCarePathSettings';
import type { IService } from '../../../../../../common/interfaces/service';
import type { IOrganizationProfile } from '../../../../interfaces/organizationAddCarePathState';
import type { IPractitioner } from '../../../../interfaces/practitioner';
import type { IReactSelectOptions } from '../../../../interfaces/reactSelectOptions';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { parseNameForSearch } from '../../../../../../common/utils/parseNameForSearch';
import { translateKey } from '../../../../../translate';
import { useAbortableFetch } from '../../../../../utils/hooks/useAbortableFetch';
import { DropdownInput } from '../../../../fields/Dropdown';
import { OrganizationAddCarePathContext } from '../../../organizations/organizationAddCarePath/organizationAddCarePathContext';
import { SettingsSearchableDropDown } from './SettingsSearchableDropDown';
import { SettingsSwitch } from './SettingsSwitch';

interface ISubsequentServicesProps {
    allServiceConsumerSettings: (IAddCarePathServiceConsumerSettings & {
        isDisabled: boolean;
        profileId: string;
        service: IService;
    })[];
    currentServiceConsumerSettings: TValueOfAddCarePathSettings;
    employmentStartDate?: string;
    error: string | undefined;
    label: string;
    listKey: string;
    onChange: (value: boolean) => void;
    onChangeSubsequentService: (autoSubsequentService: IAutoSubsequentService | null) => void;
    serviceType: ServiceType;
}

export function SubsequentServices(props: ISubsequentServicesProps) {
    const abortableFetch = useAbortableFetch();
    const { addCarePathState } = useContext(OrganizationAddCarePathContext);
    const [providerOrganizations, setProviderOrganizations] = useState<IProviderOrganization[]>([]);
    const [succeedingService, setSucceedingService] = useState<IService>();

    useEffect(() => {
        if (isSubsequentServiceDisabled() && Boolean(props.currentServiceConsumerSettings.addAutoSubsequentServices)) {
            onChange(false, 'addAutoSubsequentServices');
        }
    }, [isSubsequentServiceDisabled()]);

    useEffect(() => {
        getSucceedingService();
    }, [props.currentServiceConsumerSettings.autoSubsequentService?.subsequentServiceId]);

    if (
        !props.currentServiceConsumerSettings.subsequentServices ||
        props.currentServiceConsumerSettings.subsequentServices.length === 0
    ) {
        return null;
    }

    if (props.listKey !== 'addAutoSubsequentServices' && !props.currentServiceConsumerSettings.autoSubsequentService) {
        return null;
    }

    if (props.listKey === 'isDeviation') {
        if (props.serviceType !== ServiceType.examination) {
            return null;
        }

        const subsequentService = props.currentServiceConsumerSettings.subsequentServices.find(
            (i) => i.id === props.currentServiceConsumerSettings.autoSubsequentService?.subsequentServiceId
        );
        if (!subsequentService) {
            return null;
        }
    }

    if (
        props.listKey === 'providerOrganizationId' &&
        !props.currentServiceConsumerSettings.autoSubsequentService?.subsequentServiceId
    ) {
        return null;
    }

    if (
        props.listKey === 'providerPractitionerId' &&
        (!props.currentServiceConsumerSettings.autoSubsequentService?.providerOrganizationId ||
            isReferralOrReferralSmall())
    ) {
        return null;
    }

    return (
        <FormGroup
            isRequired={isRequired(props.listKey)}
            key={props.label + props.listKey}
            label={props.label}
            tooltipText={
                isSubsequentServiceDisabled() ? translateKey('subsequentService.subsequentServiceNeedsToBeActive') : ''
            }
        >
            {props.listKey === 'addAutoSubsequentServices' && (
                <SettingsSwitch
                    disabled={isSubsequentServiceDisabled()}
                    listKey={props.listKey}
                    onChange={(value) => onChange(value, props.listKey)}
                    value={Boolean(props.currentServiceConsumerSettings.addAutoSubsequentServices)}
                />
            )}
            {props.listKey === 'subsequentServiceId' && (
                <DropdownInput
                    error={props.error}
                    onChange={(value) => onChange(value, props.listKey)}
                    placeholder={translateKey('common.clickAndChooseFromList')}
                    selectOptions={props.currentServiceConsumerSettings.subsequentServices.map((subsequentService) => ({
                        value: subsequentService.id,
                        text: subsequentService.succeedingService.name,
                    }))}
                    value={props.currentServiceConsumerSettings.autoSubsequentService!.subsequentServiceId!}
                />
            )}
            {props.listKey === 'isDeviation' && (
                <SettingsSwitch
                    listKey={props.listKey}
                    onChange={(value) => onChange(value, props.listKey)}
                    value={Boolean(props.currentServiceConsumerSettings.autoSubsequentService?.isDeviation)}
                />
            )}
            {props.listKey === 'providerOrganizationId' &&
                props.currentServiceConsumerSettings.autoSubsequentService?.subsequentServiceId &&
                succeedingService && (
                    <SettingsSearchableDropDown
                        error={props.error}
                        loadOptions={(input) => getServiceProviderOrganizations(input)}
                        onChange={(value) => onChange(value, props.listKey)}
                        value={props.currentServiceConsumerSettings.autoSubsequentService.providerOrganizationId}
                    />
                )}
            {props.listKey === 'providerPractitionerId' &&
                props.currentServiceConsumerSettings.autoSubsequentService?.providerOrganizationId &&
                succeedingService && (
                    <SettingsSearchableDropDown
                        error={props.error}
                        key={props.currentServiceConsumerSettings.autoSubsequentService?.providerOrganizationId}
                        loadOptions={(input) =>
                            getServiceProviderPractitioners(
                                input,
                                props.currentServiceConsumerSettings.autoSubsequentService!
                            )
                        }
                        onChange={(value) => onChange(value, props.listKey)}
                        value={props.currentServiceConsumerSettings.autoSubsequentService.providerPractitionerId}
                    />
                )}
        </FormGroup>
    );

    function isRequired(listKey: string) {
        switch (listKey) {
            case 'subsequentServiceId':
            case 'providerOrganizationId':
                return true;
            case 'providerPractitionerId':
                const providerOrganization =
                    props.currentServiceConsumerSettings.autoSubsequentService?.providerOrganization;
                if (providerOrganization && succeedingService) {
                    if (
                        !providerOrganization.isOwnOrganizationRequestRecipient &&
                        !providerOrganization.isRequestRecipient
                    ) {
                        return true;
                    }
                }

                return false;
            default:
                return false;
        }
    }

    function getSucceedingService() {
        setSucceedingService(
            props.currentServiceConsumerSettings.subsequentServices.find(
                ({ id }) => id === props.currentServiceConsumerSettings.autoSubsequentService?.subsequentServiceId
            )?.succeedingService
        );
    }

    async function getServiceProviderOrganizations(searchInput: string) {
        if (!succeedingService) {
            return [];
        }

        const serviceProviderOrganizations: IProviderOrganization[] = [];

        if (isOwnOrganizationRequestRecipient(succeedingService.id)) {
            serviceProviderOrganizations.push({
                ...addCarePathState.organization!,
                isOwnOrganizationRequestRecipient: true,
            });
        }
        const searchText = searchInput ? `&searchText=${encodeURIComponent(parseNameForSearch(searchInput))}` : '';

        const isBiometrician = succeedingService.serviceType === ServiceType.examination ? '&isBiometrician=true' : '';
        const isReferral = succeedingService.serviceType === ServiceType.referral ? '&isReferral=true' : '';
        const isReferralSmall =
            succeedingService.serviceType === ServiceType.referralSmall ? '&isReferralSmall=true' : '';
        const startDate = props.employmentStartDate ? `&startDate=${props.employmentStartDate}` : '';

        const url = `/api/services/${succeedingService.id}/serviceProviders/organizationsAndPractitioners?${isBiometrician}${searchText}${isReferral}${isReferralSmall}${startDate}`;

        const result = await abortableFetch.fetchJson(url, 'GET');
        const items = result.json.items.map(
            ({
                id,
                name,
                isOrganizationRequestRecipient,
            }: {
                id: string;
                isOrganizationRequestRecipient: boolean;
                name: string;
            }) => ({ id, name, isRequestRecipient: isOrganizationRequestRecipient })
        );
        serviceProviderOrganizations.push(...items);
        setProviderOrganizations(serviceProviderOrganizations);

        return serviceProviderOrganizations.map((organization) => {
            return {
                value: organization.id,
                label: organization.name,
            };
        });
    }

    function getCurrentServiceGroup(succeedingServiceId: string) {
        return addCarePathState.serviceGroups?.find((serviceGroup) =>
            serviceGroup.services?.some((service) => service.id === succeedingServiceId)
        );
    }

    function isOwnOrganizationRequestRecipient(succeedingServiceId: string) {
        if (!addCarePathState.roles || addCarePathState.roles.length === 0) {
            return false;
        }
        const currentServiceGroup = getCurrentServiceGroup(succeedingServiceId);

        if (!currentServiceGroup || !currentServiceGroup.profiles) {
            return false;
        }

        const profilesIdWithAssignedPractitioners = addCarePathState.roles
            .filter((role) => role.serviceGroupId === currentServiceGroup.id)
            .map((role) => role.profileId);

        return Boolean(
            findEnabledProfileForSucceedingServiceId(
                succeedingServiceId,
                currentServiceGroup.profiles.filter((profile) =>
                    profilesIdWithAssignedPractitioners.some((profileId) => profile.id === profileId)
                )
            )
        );
    }

    function findEnabledProfileForSucceedingServiceId(succeedingServiceId: string, profiles: IOrganizationProfile[]) {
        return profiles.find((profile) =>
            profile.settings?.serviceProviderSettings.some(
                (setting) => setting.service.id === succeedingServiceId && !setting.isDisabled
            )
        );
    }

    function getOwnOrganizationProviderPractitioners(succeedingServiceId: string) {
        const currentServiceGroup = getCurrentServiceGroup(succeedingServiceId);

        if (!currentServiceGroup || !currentServiceGroup.profiles || !addCarePathState.roles) {
            return [];
        }

        const profilesIdWithAssignedPractitioners = addCarePathState.roles
            .filter((role) => role.serviceGroupId === currentServiceGroup.id)
            .map((role) => role.profileId);

        const profileForSucceedingService = currentServiceGroup.profiles
            .filter((profile) => profilesIdWithAssignedPractitioners.some((profileId) => profile.id === profileId))
            .find((profile) =>
                profile.settings?.serviceProviderSettings.some(
                    (setting) =>
                        setting.service.id === succeedingServiceId && !setting.isDisabled && setting.isRequestRecipient
                )
            );

        return addCarePathState.roles
            .filter(
                (role) =>
                    role.serviceGroupId === currentServiceGroup?.id &&
                    role.profileId === profileForSucceedingService?.id
            )
            .map((role) => role.employments)
            .reduce((acc, val) => acc.concat(val), []) // the compiler settings are set to ES2017 array.flat/flatMap are available in ES2019
            .map((employment) => employment.practitioner);
    }

    async function getServiceProviderPractitioners(searchText: string, item: IAutoSubsequentService) {
        if (!item.providerOrganizationId) {
            return [];
        }

        if (!succeedingService) {
            return [];
        }

        const serviceProviderPractitioners = [];

        if (item.providerOrganizationId === addCarePathState.organizationId) {
            serviceProviderPractitioners.push(...getOwnOrganizationProviderPractitioners(succeedingService.id));
        } else {
            let url = `/api/serviceProvidersList/${succeedingService.id}/practitioners?organizationId=${
                item.providerOrganizationId
            }&isRequestRecipient=true&${searchText ? `&searchText=${searchText}` : ''}`;

            if (succeedingService.serviceType === ServiceType.examination) {
                url += '&isBiometrician=true';
            }

            const result = await abortableFetch.fetchJson(url, 'GET');
            serviceProviderPractitioners.push(...result.json.items);
        }

        return serviceProviderPractitioners.map((practitioner: IPractitioner) => {
            return {
                value: practitioner.id,
                label: getDisplayName(practitioner),
            };
        });
    }

    function onChange(value: boolean | string | number | null | IReactSelectOptions, listKey: string) {
        const autoSubsequentService = () => {
            switch (listKey) {
                case 'addAutoSubsequentServices': {
                    props.onChange(Boolean(value));

                    return value ? { subsequentServiceId: null, isDeviation: false } : null;
                }
                case 'isDeviation': {
                    return {
                        ...props.currentServiceConsumerSettings.autoSubsequentService,
                        isDeviation: value,
                    };
                }
                case 'subsequentServiceId': {
                    return value
                        ? {
                              isDeviation:
                                  props.currentServiceConsumerSettings.autoSubsequentService?.isDeviation || false,
                              providerOrganizationId: null,
                              providerPractitionerId: null,
                              subsequentServiceId: value,
                          }
                        : {};
                }
                case 'providerOrganizationId': {
                    return {
                        ...props.currentServiceConsumerSettings.autoSubsequentService,
                        providerOrganization: providerOrganizations.find((organization) => organization.id === value),
                        providerOrganizationId: value,
                        providerPractitionerId: null,
                        succeedingService: { ...succeedingService },
                    };
                }
                case 'providerPractitionerId': {
                    return {
                        ...props.currentServiceConsumerSettings.autoSubsequentService,
                        providerPractitionerId: value,
                    };
                }

                default:
                    return null;
            }
        };
        props.onChangeSubsequentService(autoSubsequentService() as IAutoSubsequentService);
    }

    function isSubsequentServiceDisabled() {
        return Boolean(
            props.allServiceConsumerSettings
                .filter((serviceConsumerSetting) =>
                    props.currentServiceConsumerSettings.subsequentServices.some(
                        (subsequentService) =>
                            subsequentService.succeedingServiceId === serviceConsumerSetting.service.id
                    )
                )
                .filter((serviceConsumerSetting) => serviceConsumerSetting.isDisabled).length
        );
    }

    function isReferralOrReferralSmall() {
        return [ServiceType.referral, ServiceType.referralSmall].some(
            (serviceType) => serviceType === succeedingService?.serviceType
        );
    }
}
